/**
 * Heart on product cards. Adds or removes the product from the COA order list.
 * Same contract as the TakeAware heart: GET the list, POST to add, DELETE to remove.
 */
(function () {
  const BUTTON = ':is(.order-list-heart__button, .nf-add-to-favourite)';
  const config = window.OrderListHeartConfig || { loggedIn: false, labels: {}, maxItems: 128 };
  const labels = config.labels || {};
  const MAX_ITEMS = config.maxItems || 128;

  const HEART = 'M12 21C7.2 16.6 3.2 13.2 3.2 9.1 3.2 6.2 5.5 4 8.3 4c1.7 0 3 .9 3.7 3.6C12.7 4.9 14 4 15.7 4c2.8 0 5.1 2.2 5.1 5.1 0 4.1-4 7.5-8.8 11.9z';
  const OUTLINE = `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-wishlist" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${HEART}" fill="none" stroke="#d0d1d4" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const FILLED = `<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-wishlist" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${HEART}" fill="#ff5146" stroke="#ff5146" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const SPINNER = `<span class="order-list-heart__spinner" aria-hidden="true"></span>`;

  /** @type {Set<string>} */
  const productIds = new Set();
  /** @type {Set<string>} */
  const pending = new Set();
  let listLoaded = false;
  let listLoading = false;
  let toastTimer = 0;

  function normalizeId(id) {
    const value = String(id ?? '').trim();
    const match = value.match(/(\d+)$/);
    return match ? match[1] : value;
  }

  function coa() {
    return window.SCHELTINGA_COA || window.TAKEAWARE_COA || window.customerOrdersApp || null;
  }

  function asId(value) {
    if (value == null || value === '') return null;
    return String(value);
  }

  function identity() {
    const strings = window.String || {};
    return {
      email: strings.customerEmail || '',
      companyId: asId(strings.companyId),
      locationId: asId(strings.currentLocationID),
    };
  }

  function scope() {
    const endpoints = coa()?.urlEndpoints;
    if (endpoints?.scope === 'location' || endpoints?.scope === 'company' || endpoints?.scope === 'customer') {
      return endpoints.scope;
    }
    if (coa()?.isCompanyLocation) return 'location';
    if (coa()?.isCompany) return 'company';
    return 'customer';
  }

  function listPath() {
    const path = coa()?.urlEndpoints?.productList;
    if (typeof path === 'string' && path.startsWith('/')) return path;
    if (scope() === 'location') return '/company/location/product-list';
    if (scope() === 'company') return '/company/product-list';
    return '/customer/product-list';
  }

  // GET goes directly to Gadget (`urlApi` from the COA snippet), skipping the app proxy's ~1.2-1.9s
  // per call. POST and DELETE stay on the proxy, as in the order-list app (services/api.js).
  function apiUrl(search, method = 'GET') {
    const query = search && [...search.keys()].length ? `?${search.toString()}` : '';
    const direct = coa()?.urlApi;
    if (method === 'GET' && direct) {
      return `${direct.replace(/\/+$/, '')}${listPath()}${query}`;
    }
    const proxy = coa()?.urlProxy || '/apps/scheltinga-coa-proxy/';
    const base = proxy.endsWith('/') ? proxy : `${proxy}/`;
    return `${base}api/v1${listPath()}${query}`;
  }

  function showToast(message) {
    const toast = document.getElementById('order-list-heart-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 1600);
  }

  function paintButton(button) {
    const id = normalizeId(button.dataset.productId);
    const inList = id !== '' && productIds.has(id);
    const loading = button.dataset.loading === 'true';
    const stateKey = `${inList ? 'in' : 'out'}:${loading ? 'loading' : 'idle'}:${listLoading ? 'list' : 'ready'}`;
    button.dataset.inList = inList ? 'true' : 'false';
    button.setAttribute('aria-pressed', inList ? 'true' : 'false');
    button.setAttribute('aria-label', inList ? labels.remove || 'Remove from order list' : labels.add || 'Add to order list');
    button.disabled = loading;
    if (button.dataset.heartState === stateKey) return;
    button.dataset.heartState = stateKey;
    button.innerHTML = loading ? SPINNER : inList ? FILLED : OUTLINE;
  }

  function paintAll() {
    document.querySelectorAll(`${BUTTON}[data-product-id]`).forEach((button) => {
      paintButton(button);
    });
  }

  // The API answers 403 with one of these when the token itself is rejected (scheltinga-coa core/auth.ts).
  const TOKEN_REJECTED_MESSAGES = ['Token expired.', 'Access denied.'];

  async function isTokenRejected(response) {
    if (response.status === 401) return true;
    if (response.status !== 403) return false;
    try {
      const body = await response.clone().json();
      return TOKEN_REJECTED_MESSAGES.includes(body?.message);
    } catch {
      return false;
    }
  }

  /** Drop the cached token and fetch a new one. Works with older snippets too. */
  async function replaceRejectedToken() {
    try {
      if (typeof window.forceRefreshToken === 'function') {
        await window.forceRefreshToken();
      } else {
        window.removeSessionRefreshTokenTracking?.();
        await window.refreshTokenIfNeeded?.();
      }
    } catch {
      // Fall through with whatever token is available.
    }
  }

  async function authorizedFetch(url, options) {
    if (typeof window.refreshTokenIfNeeded === 'function') {
      await window.refreshTokenIfNeeded();
    }
    const send = (token) =>
      fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
      });

    const token = coa()?.authToken;
    if (!token) throw new Error('missing-token');
    let response = await send(token);

    // A rejected token (expired, or left over from an earlier session) is replaced once and retried.
    if (await isTokenRejected(response)) {
      await replaceRejectedToken();
      const refreshed = coa()?.authToken;
      if (refreshed) response = await send(refreshed);
    }

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response;
  }

  function readIds(payload) {
    const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
    return rows.map((row) => normalizeId(row?.id ?? row)).filter(Boolean);
  }

  async function loadList() {
    if (!config.loggedIn || listLoading || listLoaded) return;
    listLoading = true;
    paintAll();
    try {
      const { email, companyId, locationId } = identity();
      const currentScope = scope();
      const search = new URLSearchParams();
      if (email) search.set('email', email);
      if (currentScope === 'location' && locationId) search.set('company_location_id', String(locationId));
      if (currentScope === 'company' && companyId) search.set('company_id', String(companyId));
      const response = await authorizedFetch(apiUrl(search, 'GET'), { method: 'GET' });
      const payload = await response.json();
      productIds.clear();
      readIds(payload).forEach((id) => productIds.add(id));
      listLoaded = true;
    } catch (error) {
      console.error('Failed to load order list', error);
    } finally {
      listLoading = false;
      paintAll();
    }
  }

  function mutationBody(productId, method) {
    const { companyId, locationId } = identity();
    const currentScope = scope();
    if (currentScope === 'location') {
      return {
        company_location_id: locationId,
        ...(method === 'DELETE' && companyId ? { company_id: companyId } : {}),
        product_ids: [productId],
      };
    }
    if (currentScope === 'company') {
      return {
        company_id: companyId,
        product_ids: [productId],
      };
    }
    return { product_ids: [productId] };
  }

  function mutationSearch(method) {
    const { email, locationId } = identity();
    const currentScope = scope();
    const search = new URLSearchParams();
    if (method === 'POST' && currentScope !== 'customer') return search;
    if (email) search.set('email', email);
    if (method === 'DELETE' && currentScope === 'location' && locationId) {
      search.set('company_location_id', String(locationId));
    }
    return search;
  }

  async function toggle(button) {
    const productId = normalizeId(button.dataset.productId);
    if (!productId || pending.has(productId)) return;

    if (!listLoaded) await loadList();

    const inList = productIds.has(productId);
    if (!inList && productIds.size >= MAX_ITEMS) {
      showToast(labels.maxReached || 'Maximum reached');
      return;
    }

    pending.add(productId);
    document.querySelectorAll(BUTTON).forEach((node) => {
      if (normalizeId(node.dataset.productId) !== productId) return;
      node.dataset.loading = 'true';
      paintButton(node);
    });

    const method = inList ? 'DELETE' : 'POST';
    try {
      await authorizedFetch(apiUrl(mutationSearch(method), method), {
        method,
        body: JSON.stringify(mutationBody(productId, method)),
      });
      if (inList) productIds.delete(productId);
      else productIds.add(productId);
      showToast(inList ? labels.removed : labels.added);
    } catch (error) {
      console.error('Failed to update order list', error);
      showToast(labels.error || 'Could not update the order list');
    } finally {
      pending.delete(productId);
      document.querySelectorAll(BUTTON).forEach((node) => {
        if (normalizeId(node.dataset.productId) === productId) delete node.dataset.loading;
      });
      paintAll();
    }
  }

  function openLogin() {
    const dialog = document.getElementById('order-list-heart-login');
    if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(BUTTON);
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.hasAttribute('data-order-list-login') || !config.loggedIn) {
        openLogin();
        return;
      }
      toggle(button);
    },
    true
  );

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(`${BUTTON}[data-product-id]`)) paintButton(node);
        node.querySelectorAll(`${BUTTON}[data-product-id]`).forEach((button) => {
          paintButton(button);
        });
      });
    });
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    if (config.loggedIn && document.querySelector(`${BUTTON}[data-product-id]`)) {
      loadList();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
